<template>
    <v-tooltip
        ref="tooltipRef"
        v-model="isTooltipOpen"
        :disabled="mobile || disabled"
        :content-props="{
            style: {
                top: `${mousePos.y}px`,
                left: `${mousePos.x}px`,
                transform: `translate(${tooltipOffset.x}px, ${tooltipOffset.y}px)`,
                transformOrigin: tooltipOffset.origin,
                '--v-overlay-anchor-origin': tooltipOffset.origin
            }
        }"
        content-class="stop-time-tooltip rounded-lg elevation-6"
        transition="fade-transition"
        delay="400"
        @update:model-value="onTooltip"
    >
        <template #activator="{ props: activatorProps }">
            <div v-bind="activatorProps" class="stop-time-tooltip__activator" @mouseenter="onMouseEnter">
                <slot v-bind="{ mousePos, isTooltipOpen }" />
            </div>
        </template>

        <template #default>
            <div ref="tooltipContentRef" class="stop-time-tooltip__content">
                <div v-if="showExpectedTime" class="stop-time-tooltip__item">
                    <span class="stop-time-tooltip__label">{{ t('common.resources.stopTime.expectedTime', 'Expected') }}</span>
                    <span class="stop-time-tooltip__value d-flex align-center ga-1">
                        <realtime-indicator v-if="expectedTimeIsRealtime" />
                        <span :class="{ 'font-weight-bold': expectedTimeIsRealtime }">
                            {{ formatTime(expectedTime!) }}
                        </span>
                    </span>
                </div>
                <v-divider v-if="showExpectedTime" class="mx-4" />
                <div class="stop-time-tooltip__item">
                    <span class="stop-time-tooltip__label">{{ t('common.resources.stopTime.aimedTime', 'Scheduled') }}</span>
                    <span v-if="showExpectedTime" class="stop-time-tooltip__value">{{ formatTime(aimedTime) }}</span>
                </div>
            </div>
        </template>
    </v-tooltip>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, computed } from 'vue';
import { useDisplay } from 'vuetify';
import { RealtimeIndicator } from '..';
import { useI18n } from 'vue-i18n';
import { toLocaleTimeString } from '@geta/kolumbus-frontend/composables';
import { VTooltip } from 'vuetify/components';
import { useElementBounding } from '@vueuse/core';

interface Props {
    /**
     * The aimed time of the trip
     */
    aimedTime: Date;

    /**
     * The expected time of the trip
     */
    expectedTime?: Date;

    /**
     * Whether the expected time is realtime
     */
    expectedTimeIsRealtime?: boolean;

    /**
     * Whether the tooltip is disabled
     */
    disabled?: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();
const { mobile } = useDisplay();
const tooltipRef = ref<InstanceType<typeof VTooltip>>();
const tooltipContentRef = ref<HTMLDivElement>();
const isTooltipOpen = ref(false);
const mousePos = ref({ x: 0, y: 0 });
const xPointerOffset = 16;
const yPointerOffset = 12;
const viewportMargin = 12; // Minimum space between tooltip and viewport edge
const showExpectedTime = computed(() => props.expectedTime && props.expectedTimeIsRealtime);

function formatTime(time: Date) {
    return toLocaleTimeString(time);
}

const activatorEl = computed(() => tooltipRef.value?.activatorEl);
const activatorRect = useElementBounding(activatorEl, { immediate: true, windowScroll: true });
const tooltipContentRect = useElementBounding(tooltipContentRef, { immediate: true, windowScroll: true });

const tooltipOffset = computed(() => {
    if (!activatorRect)
        return {
            x: xPointerOffset,
            y: yPointerOffset,
            origin: 'bottom right'
        };

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = tooltipContentRect.width.value;
    const tooltipHeight = tooltipContentRect.height.value;

    // Calculate vertical position
    const verticalPosition =
        activatorRect.top.value + tooltipHeight + viewportMargin > viewportHeight
            ? { y: -tooltipHeight + yPointerOffset, origin: 'bottom' }
            : { y: -yPointerOffset, origin: 'top' };

    // Calculate horizontal position
    const horizontalPosition =
        activatorRect.left.value + tooltipWidth + viewportMargin > viewportWidth
            ? { x: -tooltipWidth - xPointerOffset * 2 - viewportMargin, origin: 'right' }
            : { x: xPointerOffset, origin: 'left' };

    // Update transform based on position calculations
    return {
        x: horizontalPosition.x,
        y: verticalPosition.y,
        origin: `${horizontalPosition.origin} ${verticalPosition.origin}`
    };
});

function handleMouseMove(e: MouseEvent) {
    mousePos.value = {
        x: e.clientX,
        y: e.clientY
    };
}

function onTooltipClose() {
    window.removeEventListener('mousemove', handleMouseMove);
}

function onMouseEnter(e: MouseEvent) {
    window.addEventListener('mousemove', handleMouseMove);
    handleMouseMove(e);
}

function onTooltip(isOpen: boolean) {
    if (!isOpen) {
        onTooltipClose();
    }
}

onBeforeUnmount(() => onTooltipClose());
</script>

<style lang="scss">
@use 'vuetify/settings' as *;
@use 'vuetify/lib/styles/tools/index' as tools;
@use 'sass:map';

.stop-time-tooltip {
    &.v-overlay__content {
        padding: 8px 16px;
        background-color: var(--v-color-green-lighten-3);
        color: var(--v-color-grey-darken-5);
        transition-property: opacity;
        @include tools.typography(18px, 400, 0, 24px, none);
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    &__item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 32px;
    }

    &__value {
        text-align: right;
    }
}
</style>
